import { Box, TextField, Typography } from "@mui/material";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider } from "rsuite";
import BriaIconButton from "../../../../../../../../../components/common/BriaIconButton/BriaIconButton";
import FileUploader from "../../../../../../../../../components/common/FileUploader/FileUploader";
import { useAppStore } from "../../../../../../../../../hooks/useStores";
import { BrandText } from "../../../../../../../../../models/brandText";
import { Font, defaultFont } from "../../../../../../../../../models/font";
import { FontOptions } from "../../../../../../../../../models/fontOptions";
import Close from "../../../../../../../../assets/svgs/Close.svg";
import common from "../../Common.module.scss";
import styles from "./FontsListModel.module.scss";
interface FontsListModelProps {
	index: number;
	setActiveFontIndex: (index: number | null) => void;
	fontsOption: FontOptions[];
	loadAllFonts: () => void;
	handleFontChange: (font: Font, index: number) => void;
	brandTexts: BrandText[];
	activeFontIndex: number | null;
}

const FontsListModel: React.FC<FontsListModelProps> = ({
	index,
	setActiveFontIndex,
	fontsOption,
	loadAllFonts,
	handleFontChange,
	brandTexts,
	activeFontIndex,
}) => {
	const { brandsDefinitionStore } = useAppStore();
	const { t } = useTranslation("translation", { keyPrefix: "editor.tabs.brands" });
	const [filteredOptions, setFilteredOptions] = useState(fontsOption);
	const [searchTerm, setSearchTerm] = useState("");
	const validFonts = brandTexts.filter((bt, i) => bt?.font?.src && i !== activeFontIndex);

	useEffect(() => {
		setFilteredOptions(fontsOption);
	}, [fontsOption]);

	const handleChange = (value: string) => {
		setSearchTerm(value);
		const filtered = fontsOption
			.map((fontCategory) => ({
				...fontCategory,
				options: fontCategory.options.filter((font: any) =>
					font.name.toLowerCase().includes(value.toLowerCase()),
				),
			}))
			.filter((fontCategory) => fontCategory.options.length > 0);
		setFilteredOptions(filtered);
	};

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file: File | null = e.target.files && e.target.files[0];
		e.target.value = "";
		if (file) {
			const res: any = await brandsDefinitionStore.uploadFont(
				{ ...defaultFont, name: file.name.split(".")[0] },
				file,
			);
			if (res) {
				const newFont: Font = {
					...defaultFont,
					name: file.name.split(".")[0],
					id: res.id,
					src: res.src,
				};
				handleFontChange(newFont, index);
				loadAllFonts();
				setActiveFontIndex(null);
			}
		}
	};

	return (
		<Box className={clsx(styles.fontsListContainer)} key={index}>
			<Box className={styles.listHeader}>
				<Box className={styles.title}>
					{t("font")} {index + 1}
				</Box>
				<Box className={styles.iconsContainer}>
					<BriaIconButton className={styles.actionBtn} onClick={() => setActiveFontIndex(null)}>
						<img src={Close} alt={t("closeBrand")} />
					</BriaIconButton>
				</Box>
			</Box>
			<TextField
				id={t("search")}
				placeholder={t("search")}
				value={searchTerm}
				onChange={(e) => handleChange(e.target.value)}
				className={styles.searchTextField}
				fullWidth
				InputProps={{ classes: { root: styles.searchTextField } }}
			/>
			<Box className={styles.fontsOptionWrapper}>
				<>
					{validFonts.length > 0 && <Typography className={styles.brandFonts}>{t("brandFonts")}</Typography>}
					{brandTexts.map((brandText, idx) =>
						brandText.font?.src && idx !== activeFontIndex ? (
							<Box
								onClick={() => {
									if (brandText.font) handleFontChange(brandText.font, index);
								}}
								className={styles.fontItemContent}
							>
								<Typography className={clsx(common.itemLabel, common.fontLabel)}>
									{t("font")} {idx + 1}
								</Typography>
								<Typography style={{ fontFamily: brandText.font?.name }}>
									{brandText.font?.name}
								</Typography>
							</Box>
						) : null,
					)}
					{validFonts.length > 0 && <Divider className={styles.divider} />}
				</>

				{filteredOptions
					?.sort((a: FontOptions, b: FontOptions) => a.name.localeCompare(b.name))
					.map((group: FontOptions, groupIndex: number) => (
						<Box className={styles.fontContainer} key={groupIndex}>
							<Typography className={styles.fontName}>{group.name}</Typography>
							<Box className={styles.fontOptions}>
								{group.options
									.sort((a: Font, b: Font) => a.name?.localeCompare(b.name))
									.map((font: Font, fontIndex: number) => (
										<Box
											className={styles.fontItem}
											sx={{ fontFamily: font.name }}
											key={fontIndex}
											onClick={() => handleFontChange(font, index)}
										>
											{font.name}
										</Box>
									))}
							</Box>
							{groupIndex === 0 && <Divider className={styles.divider} />}
						</Box>
					))}
			</Box>
			<Box className={styles.fileUploaderContainer}>
				<FileUploader
					buttonText={t("uploadCustomFont")}
					buttonType="secondaryMedium"
					inputProps={{ accept: ".ttf" }}
					onUpload={handleFileChange}
					buttonProps={{ className: styles.uploadBtn }}
					loading={brandsDefinitionStore.loadingUpload}
				/>
				<Typography className={styles.infoMessage}>{t("supportsttfFiles")}</Typography>
			</Box>
		</Box>
	);
};

export default FontsListModel;
